import React, { useState, useEffect } from "react";
import ImageCropper from "../components/ImageCropper";

export default function UploadPage() {
  const [imageCards, setImageCards] = useState([]);
  const [maxImages, setMaxImages] = useState(null);
  const [submitted, setSubmitted] = useState(false); // Track if images already submitted
  const [uploading, setUploading] = useState(false); // Prevent double submission

  const allCropped = imageCards.length > 0 && imageCards.every(card => card.cropped);

  const hash = window.location.pathname.split("/").pop();

  // Fetch order info to validate hash, get max images, and check submission
  useEffect(() => {
    async function fetchOrder() {
      try {
        const res = await fetch(`${process.env.REACT_APP_BASE_URL || ""}/api/upload/${hash}`);
        if (!res.ok) throw new Error("Invalid upload link");
        const data = await res.json();
        
        // Check if images already uploaded
        if (data.submitted) {
        setSubmitted(true);
        setImageCards([]); // hide upload cards
        return;
        }

        setMaxImages(data.maxImages);

        // Initialize empty cards
        setImageCards(Array.from({ length: data.maxImages }, () => ({
          file: null,
          fileurl: null,
          cropped: null,
        })));
      } catch (err) {
        alert(err.message);
      }
    }
    fetchOrder();
  }, [hash]);

  const handleFileChange = (index, file) => {
    const url = URL.createObjectURL(file);
    setImageCards((prev) => {
      const newCards = [...prev];
      if (newCards[index].fileurl) {
        URL.revokeObjectURL(newCards[index].fileurl); // Clean up old URL
      }
      newCards[index] = {file, fileurl: url, cropped: null};
      return newCards;
    });
  };

  const handleCropped = (index, blob) => {
    setImageCards((prev) => {
      const newCards = [...prev];
      newCards[index].cropped = blob;
      return newCards;
    });
  };

  const removeFile = (index) => {
    setImageCards(prev => {
      const newCards = [...prev];
      if (newCards[index].fileurl) {
        URL.revokeObjectURL(newCards[index].fileurl); // Clean up URL
      }
      newCards[index] = { file: null, fileurl: null, cropped: null };
      return newCards;
    });
  };

  const handleUpload = async () => {
   if (submitted || uploading) return; // Prevent upload if already submitted or currently uploading
    setUploading(true);

    const croppedFiles = imageCards
      .filter(card => card.cropped)
      .map((card, i) => ({ blob: card.cropped, name: `image-${i}.png` }));

    if (croppedFiles.length === 0) {
      alert("No images selected or cropped");
      setUploading(false);
      return;
    }

    const formData = new FormData();
    croppedFiles.forEach(f => formData.append("images", f.blob, f.name));

    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || ""}/api/upload/${hash}`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true); //Mark as submitted
        alert("Images submitted successfully! Thank you.");
      } else {
        alert(data.error || "Upload failed");
      }
    } catch (err) {
      console.error(err);
      alert("Upload failed: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto mt-10 p-6 bg-white shadow rounded">
      <h2 className="text-2xl font-bold mb-4">Upload Your Images</h2>

      {submitted ? (
        <p className="mb-4 text-green-600 font-semibold">
          You have already submitted your images. Thank you!
        </p>
      ) : (
        maxImages && <p className="mb-4">Upload up to {maxImages} images for this order.</p>
      )}

      <div className="space-y-6">
        {imageCards.map((card, index) => (
          <div key={index} className="border p-4 rounded shadow">
            <div className="flex justify-between items-center mb-2">
              <p>Image {index + 1}</p>
              {card.file && !submitted && (
                <button
                  onClick={() => removeFile(index)}
                  className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Remove
                </button>
              )}
            </div>
            {!card.file ? (
              <input
                type="file"
                accept="image/*"
                disabled={submitted}
                onChange={(e) => e.target.files[0] && handleFileChange(index, e.target.files[0])}
                className="border p-2 w-full"
              />
            ) : (
              <>
                <ImageCropper
                  file={card.file}
                  imageUrl={card.fileurl}
                  onCropped={(blob) => handleCropped(index, blob)}
                  disabled={submitted} // Disable cropping if submitted
                />
                {card.cropped && (
                  <img
                    src={URL.createObjectURL(card.cropped)}
                    alt="Cropped preview"
                    className="mt-2 max-h-40 border rounded"
                  />
                )}
              </>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={handleUpload}
        disabled={submitted || !allCropped || uploading} // disable if already submitted OR not all cropped
        className={`mt-6 px-6 py-2 text-white rounded ${
          submitted || !allCropped ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
        }`}
      >
        Submit Upload
      </button>
    </div>
  );
}
